import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { AdditionalAssetMeta } from "@amzn/afxtaco-types";
import { reducerType } from "../../../../redux/reducers";
import { isExecutionActive } from "../../../../redux/reducers/Execution";
import { useAssetManifest } from "../../../hooks/useAssetManifest";
import { useUploadedFiles } from "../../../hooks/useUploadedFiles";
import { extractSection } from "../../../utils/markdown";
import {
  buildDeliverables,
  countChangedToday,
  CanonicalDeliverableSpec,
  Deliverable,
  generatedDeliverables,
} from "../utils/deliverables";
import {
  buildChecklist,
  DELIVERY_SECTION_HEADING,
  DeliveryItem,
} from "../utils/deliveryChecklist";
import type { ProjectState } from "../ProjectStateCallout";

interface UseOverviewDataOptions {
  projectId: string | undefined;
  /** Canonical artifacts to show, with their tab values (feature-flag aware). */
  canonicalSpecs: CanonicalDeliverableSpec[];
  /** Additional assets already fetched by the page, to avoid a second request. */
  additionalAssets: AdditionalAssetMeta[];
  additionalLabel: (filename: string) => string;
  additionalTabValue: (path: string) => string;
  /** `summary.md` contents, already fetched by the page. */
  summaryContent: string | undefined;
  /**
   * Contents of the structured delivery-definition asset, already fetched by the
   * page. Preferred over parsing the prose section out of `summaryContent`.
   */
  deliveryDefinitionContent: string | undefined;
  /**
   * `Project.latestSessionLastActivityAt` — when TACO last ran a turn. The
   * orchestrator stamps it at both turn start and turn completion, with no
   * conditional skip, so it is the one reliable record of agent activity.
   *
   * Only `getProject` returns it, so it can be absent on a record hydrated from
   * the projects list.
   */
  sessionLastActivityAt: string | undefined;
  /** Skip fetching (the new-project flow, before anything exists in S3). */
  skip?: boolean;
}

interface UseOverviewDataResult {
  deliverables: Deliverable[];
  deliverableCount: number | undefined;
  changedTodayCount: number | undefined;
  isLoadingDeliverables: boolean;
  deliveryItems: DeliveryItem[];
  uploadedFiles: ReturnType<typeof useUploadedFiles>["files"];
  isLoadingUploads: boolean;
  projectState: ProjectState;
}

/**
 * Aggregates everything the Overview tab renders into one view model.
 *
 * Exists so `OverviewTab` doesn't call several hooks and derive state inline —
 * it keeps the container thin and leaves every child component purely
 * presentational, which is what makes them testable without a store.
 */
export const useOverviewData = ({
  projectId,
  canonicalSpecs,
  additionalAssets,
  additionalLabel,
  additionalTabValue,
  summaryContent,
  deliveryDefinitionContent,
  sessionLastActivityAt,
  skip = false,
}: UseOverviewDataOptions): UseOverviewDataResult => {
  const { assets: manifest, isLoading: isLoadingManifest } = useAssetManifest({
    projectId,
    skip,
  });

  const { files: uploadedFiles, isLoading: isLoadingUploads } =
    useUploadedFiles({ projectId, skip });

  const executionStatus = useSelector(
    (state: reducerType) => state.Execution.status,
  );
  const executionProjectId = useSelector(
    (state: reducerType) => state.Execution.projectId,
  );

  const deliverables = useMemo(
    () =>
      buildDeliverables({
        manifest,
        additionalAssets,
        canonicalSpecs,
        additionalLabel,
        additionalTabValue,
      }),
    [
      manifest,
      additionalAssets,
      canonicalSpecs,
      additionalLabel,
      additionalTabValue,
    ],
  );

  const generated = useMemo(
    () => generatedDeliverables(deliverables),
    [deliverables],
  );

  // Counts stay undefined until the manifest first lands, so the strip renders
  // "—" rather than briefly claiming zero files on a project that has plenty.
  //
  // Tracked per project rather than as a plain `!isLoadingManifest`, so that a
  // *re*-fetch doesn't un-settle what is already on screen. Reverting mid-session
  // sent counts back to "—" and swapped deliverable rows for skeletons, which
  // read as the whole page reloading rather than a value updating in place.
  const manifestLoadedForRef = useRef<string | undefined>(undefined);
  if (!isLoadingManifest && projectId) manifestLoadedForRef.current = projectId;
  const settled = manifestLoadedForRef.current === projectId;

  const isThisProjectExecuting =
    executionProjectId === projectId && isExecutionActive(executionStatus);

  // Whether TACO has ever run here. Derived from the session timestamp rather
  // than the presence of `thinking/thinking.md`: that asset is being retired, and
  // the session stamp is a first-class record of the same fact.
  const hasPriorActivity = Boolean(sessionLastActivityAt);

  const deliveryItems = useMemo(
    () =>
      buildChecklist({
        definitionJson: deliveryDefinitionContent,
        sectionBody: extractSection(summaryContent, DELIVERY_SECTION_HEADING),
        // Paths tick items the agent mapped to a file; labels serve the fallback
        // matcher for items it has not mapped yet.
        existingPaths: generated
          .map((d) => d.path)
          .filter((p): p is string => Boolean(p)),
        existingLabels: generated.map((d) => d.label),
      }),
    [deliveryDefinitionContent, summaryContent, generated],
  );

  // A project with activity but no files is TACO scoping before it builds — its
  // designed first-turn behaviour — not a failure. Distinguished from a genuinely
  // untouched project so the callout can say which it is.
  const projectState: ProjectState = isThisProjectExecuting
    ? "working"
    : !settled || generated.length > 0
      ? "resting"
      : hasPriorActivity
        ? "awaitingFirstBuild"
        : "fresh";

  return {
    deliverables,
    deliverableCount: settled ? generated.length : undefined,
    changedTodayCount: settled ? countChangedToday(generated) : undefined,
    isLoadingDeliverables: isLoadingManifest && !settled,
    deliveryItems,
    uploadedFiles,
    isLoadingUploads,
    projectState,
  };
};
